// eslint-disable-next-line no-restricted-imports
import { TFunction } from 'i18next';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { createOutlineBadgeColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/OutlineBadgeColumn';
import { DealClassifierTct } from '@dealstack/shared/lib/generated';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { Button, Flex, Menu } from '@mantine/core';
import {
	StyledMenuDropdown,
	StyledMenuItem,
} from '@dealstack/frontend/shared/components/Documents/DocumentsTable/styled';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ExpandableNameCell } from './Components/ExpandableNameCell';

interface Props {
	tAdminDashboard: TFunction;
	tCommonUi: TFunction;
	onRowClick: (classifier: DealClassifierTct) => void;
}

const createClassifiersActionColumn = ({
	tAdminDashboard,
	onRowClick,
}: Props): ExtendedColumnDef<DealClassifierTct> => {
	return {
		cell: ({ row }) => {
			return (
				<Flex gap=".25rem" align="center" justify="flex-end" m="-.25rem 0" w="100%">
					<Menu>
						<Menu.Target>
							<Button
								variant="subtle"
								color="gray"
								onClick={(event) => {
									event.stopPropagation();
								}}
							>
								<Icon variant="holding-group" size="lg" />
							</Button>
						</Menu.Target>
						<StyledMenuDropdown>
							<StyledMenuItem
								onClick={() => onRowClick(row.original)}
								leftSection={<Icon variant="information" size="lg" color="black" />}
							>
								{tAdminDashboard('documents.list.actions.update')}
							</StyledMenuItem>
						</StyledMenuDropdown>
					</Menu>
				</Flex>
			);
		},
		header: '',
		id: 'actions',
		enableGlobalFilter: false,
		enableColumnFilter: false,
		showFilter: false,
		size: 80,
	};
};

export const getClassifierTableColumns: ({
	tAdminDashboard,
	tCommonUi,
	onRowClick,
}: Props) => ExtendedColumnDef<DealClassifierTct>[] = ({
	tAdminDashboard,
	tCommonUi,
	onRowClick,
}) => [
	createTextColumn({
		headerText: tAdminDashboard('deal_settings.classifier_settings.table.headers.name'),
		accessorKey: 'name',
		enableColumnFilter: false,
		enableSorting: true,
		cell: ({ row, getValue }) => <ExpandableNameCell value={getValue<string>()} row={row} />,
	}),
	createTextColumn({
		headerText: tAdminDashboard(
			'deal_settings.classifier_settings.table.headers.nbr_documents'
		),
		accessorKey: 'nbrDocuments',
		enableColumnFilter: false,
		enableSorting: true,
	}),
	createOutlineBadgeColumn({
		headerText: tAdminDashboard('deal_settings.classifier_settings.table.headers.active'),
		accessorKey: 'deleted',
		enableSorting: true,
		filterVariant: 'multi-select',
		mantineFilterMultiSelectProps: {
			data: [
				{
					label: tCommonUi('classifier.badge.true'),
					value: 'true',
				},
				{
					label: tCommonUi('classifier.badge.false'),
					value: 'false',
				},
			],
		},
		badgeMapping: {
			// enabled
			false: {
				backgroundColor: 'teal.8',
				text: tCommonUi('classifier.badge.true'),
				fontColor: 'teal.8',
			},
			// active
			true: {
				backgroundColor: 'orange.6',
				text: tCommonUi('classifier.badge.false'),
				fontColor: 'orange.6',
			},
		},
		showFooter: false,
		showFilter: false,
		filterText: tAdminDashboard('transactions.document.table.filter.badge.filter-header'),
		text: '',
	}),
	createClassifiersActionColumn({ onRowClick, tAdminDashboard, tCommonUi }),
];
